import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import { Tooltip } from 'components/Widget/Tooltip';
import { useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as S from './styles';

const PROMPT_PREVIEW_LENGTH = 120;

interface Props {
	icon: IconType;
	label: string;
	loadingLabel?: string;
	isLoading?: boolean;
	disabledReason?: string;
	prompt?: string;
	onClick: () => void;
}

const ActionButton = ({
	icon,
	label,
	loadingLabel,
	isLoading,
	disabledReason,
	prompt,
	onClick,
}: Props) => {
	const disabled = !!disabledReason;
	const theme = useTheme();
	const tooltipId = useId();
	const isMobile = useSelector((r: RootReducer) => selectIsMobile(r.mode));
	const [isExpanded, setIsExpanded] = useState(false);
	const [copied, setCopied] = useState(false);

	const hasDetail = !!prompt;
	const isLongPrompt = !!prompt && prompt.length > PROMPT_PREVIEW_LENGTH;

	const onCopyPrompt = async (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!prompt) return;
		try {
			await navigator.clipboard.writeText(prompt);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// ignore
		}
	};

	const showLoader = disabled || !!isLoading;
	const buttonLabel = disabled ? label : isLoading ? loadingLabel ?? 'Working…' : label;
	const onButtonClick = disabled || isLoading ? undefined : onClick;
	const tooltipProps = disabledReason
		? { 'data-tooltip-id': tooltipId, 'data-tooltip-content': disabledReason }
		: {};

	return (
		<S.Base>
			<S.Button
				isMobile={isMobile}
				$hasDetail={hasDetail}
				$disabled={disabled}
				onClick={onButtonClick}
				{...tooltipProps}
			>
				<S.IconWrap>
					{showLoader ? (
						<Loader size={14} />
					) : (
						<Icon type={icon} width={14} height={14} color={theme.blue[500]} />
					)}
				</S.IconWrap>
				<S.Label>{buttonLabel}</S.Label>
			</S.Button>
			{disabledReason && <Tooltip id={tooltipId} placement="bottom" />}
			{prompt && (
				<S.Detail isMobile={isMobile}>
					<S.DetailContent $expanded={isExpanded}>{prompt}</S.DetailContent>
				</S.Detail>
			)}
			{prompt && (
				<S.DetailActions>
					{isLongPrompt && (
						<S.DetailButton onClick={() => setIsExpanded(!isExpanded)}>
							{isExpanded ? 'Less' : 'More'}
						</S.DetailButton>
					)}
					<S.DetailButton onClick={onCopyPrompt}>
						<Icon
							type={copied ? IconType.CHECK : IconType.COPY}
							width={12}
							height={12}
							color={copied ? theme.gray[1000] : theme.gray[600]}
						/>
						{copied ? 'Copied' : ''}
					</S.DetailButton>
				</S.DetailActions>
			)}
		</S.Base>
	);
};

export default ActionButton;
